import {
  cloneElement,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useId,
} from 'react';

export interface FormFieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Wrapper label + hint + error dla custom pól (np. DatePicker, MaskedInput), które nie mają wbudowanych
 * props `label`/`error` jak nasz `Input`/`Select`/`Checkbox`.
 *
 * Pojedynczy ReactElement jako child dostaje wstrzyknięte `id`, `aria-invalid`, `aria-describedby`.
 * Jeśli child jest tekstem / listą elementów — FormField renderuje label oddzielnie (bez wstrzykiwania).
 */
export function FormField({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
  className,
}: FormFieldProps) {
  const autoId = useId();
  const inputId = htmlFor ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  // Guard: do not clone into Fragment (<> or <Fragment>) — Fragments reject `id`,
  // `aria-invalid`, `aria-describedby` and React warns when they are passed.
  const isFragment = isValidElement(children) && (children as ReactElement).type === Fragment;
  const enhancedChild =
    isValidElement(children) && !htmlFor && !isFragment
      ? cloneElement(children as ReactElement<Record<string, unknown>>, {
          id: inputId,
          'aria-invalid': error ? true : undefined,
          'aria-describedby': describedBy,
        })
      : children;

  // Czerwona ramka pola przy błędzie — koloruje kontrolki potomne (input/select/
  // textarea) w jednym miejscu, tak jak prymityw `<Field>` w dokumentach/kartotekach.
  // Egzotyczne widgety renderujące własny kontener (nie natywny input) pokazują błąd
  // tekstem pod polem; ramkę dostają natywne kontrolki (typy z regułami: tekst/liczba/
  // data/select) — to pokrywa formularze rekordów modułów.
  const errorBorder = error
    ? '[&_input]:border-red-500 [&_select]:border-red-500 [&_textarea]:border-red-500'
    : '';

  return (
    <div className={['flex flex-col gap-1', errorBorder, className ?? ''].join(' ')}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-text-secondary">
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}
      {enhancedChild}
      {hint && !error && (
        <span id={hintId} className="text-xs text-text-muted">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="text-xs text-red-600" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
